import React, { useEffect, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import Input from "../../../components/Input/Input";

export default function GoalsField({
  options = [],
  goals = [],
  customGoals = [],
  invalidGoals = false,
  invalidCustomGoals = false,
  onGoalsChange,
  onCustomGoalsChange,
  onInvalidPatch,
  onDuplicateGoal,
  resetKey = "",
}) {
  const [showCustomGoalInput, setShowCustomGoalInput] = useState(false);
  const [customGoalDraft, setCustomGoalDraft] = useState("");

  useEffect(() => {
    setShowCustomGoalInput(false);
    setCustomGoalDraft("");
  }, [resetKey]);

  const toggleGoal = (goal) => {
    const alreadySelected = goals.includes(goal);
    const updatedGoals = alreadySelected
      ? goals.filter((g) => g !== goal)
      : [...goals, goal];

    onGoalsChange?.(updatedGoals);

    if (updatedGoals.length > 0 || customGoals.length > 0) {
      onInvalidPatch?.({ goals: false });
    }
  };

  const addDraftGoal = () => {
    const trimmed = customGoalDraft.trim();
    if (!trimmed) return;

    const isDup =
      customGoals.some((cg) => cg.toLowerCase() === trimmed.toLowerCase()) ||
      goals.some((g) => g.toLowerCase() === trimmed.toLowerCase());

    if (isDup) {
      onDuplicateGoal?.();
      return;
    }

    onCustomGoalsChange?.([...customGoals, trimmed]);
    setCustomGoalDraft("");
    onInvalidPatch?.({ goals: false, customGoals: false });
  };

  return (
    <div data-field="goals" className={`pm-field ${invalidGoals ? "is-invalid" : ""}`}>
      <label>What is the goal of this conversation?</label>
      <div className="pm-goals-row">
        <div className="pm-goals-scroll">
          {options
            .filter((g) => g !== "Custom goal")
            .map((goal) => {
              const selected = goals.includes(goal);
              return (
                <button
                  key={goal}
                  type="button"
                  className={`pm-template-badge ${selected ? "selected" : ""}`}
                  aria-pressed={selected}
                  onClick={() => toggleGoal(goal)}
                >
                  {selected && <Check className="pm-check-icon" aria-hidden="true" />}
                  {goal}
                </button>
              );
            })}
        </div>

        <button
          type="button"
          className={`pm-template-badge pm-add-goal-btn ${showCustomGoalInput ? "selected" : ""}`}
          aria-pressed={showCustomGoalInput}
          onClick={() => setShowCustomGoalInput((v) => !v)}
        >
          {showCustomGoalInput ? (
            <Check className="pm-check-icon" aria-hidden="true" />
          ) : (
            <Plus size={18} className="pm-plus-icon" aria-hidden="true" />
          )}
          Add
        </button>
      </div>

      {showCustomGoalInput && (
        <div className="pm-field" style={{ marginTop: 10 }}>
          <label htmlFor="customGoalDraft">Describe your Goal</label>

          <div className="pm-custom-goal-inline">
            <Input
              id="customGoalDraft"
              name="customGoalDraft"
              placeholder="e.g., Speak more confidently"
              value={customGoalDraft}
              onChange={(e) => setCustomGoalDraft(e.target.value)}
              clearable
              error={invalidCustomGoals && !customGoalDraft.trim()}
            />
            <button type="button" className="pm-add-btn" onClick={addDraftGoal}>
              Add <Plus size={18} />
            </button>
          </div>

          {customGoals.length > 0 && (
            <div className="pm-custom-goals-list" style={{ marginTop: 8 }}>
              {customGoals.map((cg, i) => (
                <div key={i} className="pm-custom-goal-row">
                  <Input
                    value={cg}
                    onChange={(e) => {
                      const next = [...customGoals];
                      next[i] = e.target.value;
                      onCustomGoalsChange?.(next);
                    }}
                    clearable
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addDraftGoal();
                    }}
                    placeholder={`Custom goal ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="pm-remove-doc"
                    onClick={() => {
                      const next = customGoals.filter((_, j) => j !== i);
                      onCustomGoalsChange?.(next);
                    }}
                    aria-label="Remove custom goal"
                    title="Remove"
                  >
                    <X />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
